import {
  Button,
  Stack,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  type StepIconProps,
  DialogContent,
  Dialog,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { X } from 'lucide-react'
import { useState } from 'react'

import { useBulkImportStore } from '@/components/BulkImportModal/hooks/useBulkImportStore'
import { ReactComponent as CheckCirclePink } from '@/assets/icons/check-circle-pink.svg'
import { useBuilderVideos } from '@/views/NewBuilder/hooks/useBuilderVideos'
import { ReactComponent as Import } from '@/assets/icons/bulk-create-white.svg'

const steps = ['Select Page', 'Add Content', 'Customize']

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 12,
    left: 'calc(-50% + 20px)',
    right: 'calc(50% + 20px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#C11574', // Active/Completed connector line color
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#C11574', // Active/Completed connector line color
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#EAECF0', // Incomplete connector line color
    borderTopWidth: 1,
    borderRadius: 1,
  },
}))

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean }
}>(({ theme, ownerState }) => ({
  zIndex: 1,
  color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : '#667085', // Default/Incomplete text color
  width: 'auto', // Auto width to fit content
  minWidth: 120, // Keep a minimum width
  height: 'auto', // Let height be determined by content + padding
  display: 'flex',
  borderRadius: '9999px', // --radius-full
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid',
  borderColor: '#EAECF0', // Incomplete border --Component-colors-Utility-Gray-utility-gray-200
  background: '#F9FAFB', // Incomplete background --Component-colors-Utility-Gray-utility-gray-50
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: '12px', // Set font size
  paddingTop: '2px', // Set vertical padding
  paddingBottom: '2px', // Set vertical padding
  paddingLeft: theme.spacing(1), // Set horizontal padding (8px)
  paddingRight: theme.spacing(1), // Set horizontal padding (8px)
  gap: theme.spacing(0.5), // Set gap (4px)

  ...(ownerState.active && {
    color: '#C11574', // Active text color
    borderColor: '#DD2590', // Active border --Component-colors-Utility-Brand-utility-brand-600
    background: theme.palette.common.white, // Active step has white background in image
    boxShadow: 'none', // Active step has no shadow in the image
    fontWeight: 600, // Make active text semibold directly on the root
  }),
  ...(ownerState.completed && {
    color: '#C11574', // Completed text color
    borderColor: '#EE46BC', // Completed border --Colors-Border-border-brand-solid
    background: '#FDF2FA', // Completed background --Component-colors-Utility-Brand-utility-brand-50
    boxShadow: '0px 0px 2px 0px #FF8EDD', // Completed shadow
    fontWeight: 600, // Make completed text semibold directly on the root
  }),
}))

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className, icon } = props
  const stepNumber = Number(icon) // Convert icon to number (1-based)
  const stepIndex = stepNumber - 1 // Get 0-based index

  return (
    <ColorlibStepIconRoot className={className} ownerState={{ completed, active }}>
      {`${stepNumber.toString()}. ${steps[stepIndex]}`}
      {completed ? <CheckCirclePink /> : null}
    </ColorlibStepIconRoot>
  )
}

export function HeaderBulkImport() {
  const step = useBulkImportStore(store => store.step)

  const [openDialogHelp, setOpenDialogHelp] = useState(false)

  return (
    <Stack
      sx={{
        flexDirection: 'column',
        gap: '20px',
        paddingTop: '20px',
        paddingX: '20px',
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            gap: '10px',
            alignItems: 'center',

          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              // padding: '15px',
              borderRadius: '7.2px',
              background: '#FFE6D5',
              width: '30px',
              height: '30px',
            }}
          >
            <Stack bgcolor="#DC6803" borderRadius={'50%'} width={'16px'} height={'16px'} alignItems={'center'} justifyContent={'center'}>
              <Import width={8} height={8} />
            </Stack>
          </Stack>
          <Stack>
            <Typography fontSize={'16px'} fontWeight={600} lineHeight={'24px'}>
              Bulk Create
            </Typography>
          </Stack>
        </Stack>
        <Stepper
          alternativeLabel // Ensures labels are below icons and connector positioning works
          activeStep={step - 1}
          connector={<ColorlibConnector />}
          sx={{
            width: '100%',
            maxWidth: '600px',
          }}
        >
          {steps.map(label => (
            <Step key={label}>
              {/* Pass the custom icon component and remove label content */}
              <StepLabel
                StepIconComponent={ColorlibStepIcon}
                sx={{
                  // Remove label-specific styling as it's handled in the icon now
                  '& .MuiStepLabel-label': {
                    display: 'none', // Hide the default label text
                  },
                }}
              />{' '}
              {/* Remove label text here */}
            </Step>
          ))}
        </Stepper>
        <Stack>
          <Button
            onClick={() => {
              setOpenDialogHelp(true)
            }}
          >
            Need help?
          </Button>
        </Stack>
      </Stack>
      <DialogHelp
        open={openDialogHelp}
        onClose={() => {
          setOpenDialogHelp(false)
        }}
      />
    </Stack>
  )
}

function DialogHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { builderVideos, generateVideoUrl } = useBuilderVideos()

  const bulkVideo = builderVideos?.find(video => video.type === 'bulk_creator')

  return (
    <Dialog
      maxWidth="md"
      open={open}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '12px',
          background: '#FFF',
          boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
          width: '100%',
        },
      }}
      onClose={onClose}
    >
      <DialogContent
        sx={{
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
          padding: '40px',
        }}
      >
        <X
          color="#98A2B3"
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            cursor: 'pointer',
          }}
          onClick={onClose}
        />

        <Stack gap="20px">
          <Typography fontSize={18} fontWeight="semibold">
            {bulkVideo?.title}
          </Typography>
          <iframe
            src={generateVideoUrl(bulkVideo?.videoUrl ?? '')}
            style={{
              width: '100%',

              borderRadius: '8px',
              aspectRatio: '16/9',
            }}
            title={bulkVideo?.title}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
